import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import {
	SavedSearchQueryService,
	type SavedSearchModel,
	type SavedSearchVisibility,
} from '@supply-mover-2-0/core';
import type { SortField } from '@supply-mover-2-0/shared';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { DeleteConfirmationModal } from '../../../components/dialogs/DeleteConfirmationModal';
import { Button } from '../../../components/forms/Button';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { Toast } from '../../../services/ToastService';
import { useShareableRoles } from '../../hooks/useShareableRoles';
import { RoleSelector } from './RoleSelector';

export interface SaveNewListModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (savedSearch: SavedSearchModel) => void;
	/** Called when a saved search is successfully deleted */
	onDelete?: () => void;
	listKey: string;
	filterState: Record<string, unknown>;
	sortId?: string | null;
	sortFields?: Array<SortField> | null;
	/** Column configuration to save with this list */
	columns?: Array<unknown>;
	/** List-level overrides (showFiltersByDefault, curated sortOptions) to copy. */
	listOverrides?: Record<string, unknown> | null;
	/** Base list ID to associate with this saved search */
	baseListId?: string;
	/** If provided, modal is in "edit" mode */
	editingSavedSearch?: SavedSearchModel | null;
}

interface FormData {
	name: string;
	description: string;
	visibility: SavedSearchVisibility;
	allowEdit: boolean;
	roleIds: Array<number>;
}

const getDefaultFormData = (): FormData => ({
	name: '',
	description: '',
	visibility: 'private',
	allowEdit: false,
	roleIds: [],
});

/**
 * Modal for creating or editing a saved search.
 * Supports private, organization-wide, or role-based sharing.
 */
export function SaveNewListModal({
	isOpen,
	onClose,
	onSave,
	onDelete,
	listKey,
	filterState,
	sortId,
	sortFields,
	columns,
	listOverrides,
	baseListId,
	editingSavedSearch,
}: SaveNewListModalProps): ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const { roles: availableRoles, canShareWithAny } = useShareableRoles();

	const [formData, setFormData] = useState<FormData>(getDefaultFormData);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const isEditMode = editingSavedSearch != null;
	const isManager = editingSavedSearch?.isManager ?? true;

	// Populate form when editing
	useEffect(() => {
		if (isOpen && editingSavedSearch != null) {
			setFormData({
				name: editingSavedSearch.name,
				description: editingSavedSearch.description ?? '',
				visibility: editingSavedSearch.visibility,
				allowEdit: editingSavedSearch.allowEdit,
				roleIds: editingSavedSearch.sharedRoles.map((r) => r.id),
			});
		} else if (isOpen && editingSavedSearch == null) {
			setFormData(getDefaultFormData());
		}
		setErrors({});
	}, [isOpen, editingSavedSearch]);

	const handleChange = useCallback(
		<K extends keyof FormData>(field: K, value: FormData[K]) => {
			setFormData((prev) => ({ ...prev, [field]: value }));
			if (errors[field] != null) {
				setErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[field];
					return newErrors;
				});
			}
		},
		[errors],
	);

	const validate = useCallback((): boolean => {
		const newErrors: Record<string, string> = {};

		if (formData.name.trim().length === 0) {
			newErrors['name'] = t('Name is required');
		}

		if (formData.visibility === 'role' && formData.roleIds.length === 0) {
			newErrors['roleIds'] = t('Please select at least one role');
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [formData, t]);

	const handleSave = useCallback(async () => {
		if (!validate()) {
			return;
		}

		setIsSaving(true);

		try {
			let result: {
				data: SavedSearchModel | null;
				success: boolean;
				error?: string;
			};

			if (isEditMode && editingSavedSearch != null) {
				// Update existing
				result = await SavedSearchQueryService.update(
					editingSavedSearch.id as number,
					{
						name: formData.name,
						description:
							formData.description.length > 0 ? formData.description : null,
						...(isManager
							? {
									visibility: formData.visibility,
									allowEdit: formData.allowEdit,
									roleIds:
										formData.visibility === 'role'
											? formData.roleIds
											: undefined,
								}
							: {}),
					},
				);
			} else {
				// Create new
				result = await SavedSearchQueryService.create({
					name: formData.name,
					description:
						formData.description.length > 0 ? formData.description : undefined,
					listKey,
					baseListId,
					filterState,
					sortId: sortId ?? undefined,
					sortFields: sortFields ?? undefined,
					columns,
					listOverrides: listOverrides ?? undefined,
					visibility: formData.visibility,
					allowEdit: formData.allowEdit,
					roleIds:
						formData.visibility === 'role' ? formData.roleIds : undefined,
				});
			}

			if (result.success && result.data != null) {
				onSave(result.data);
				onClose();
			} else {
				const errorMessage = result.error ?? t('Failed to save search');
				setErrors({ submit: errorMessage });
				Toast.error(errorMessage);
			}
		} catch (error) {
			setErrors({
				submit:
					error instanceof Error ? error.message : t('Failed to save search'),
			});
		} finally {
			setIsSaving(false);
		}
	}, [
		validate,
		isEditMode,
		editingSavedSearch,
		isManager,
		formData,
		listKey,
		baseListId,
		filterState,
		sortId,
		sortFields,
		columns,
		listOverrides,
		onSave,
		onClose,
		t,
	]);

	const handleDelete = useCallback(async () => {
		if (editingSavedSearch == null || !isManager) {
			return;
		}

		setIsSaving(true);

		try {
			const result = await SavedSearchQueryService.delete(
				editingSavedSearch.id as number,
			);

			if (result.success) {
				setIsDeleteModalOpen(false);
				onDelete?.();
				onClose();
			} else {
				setErrors({ submit: result.error ?? t('Failed to delete search') });
			}
		} catch (error) {
			setErrors({
				submit:
					error instanceof Error ? error.message : t('Failed to delete search'),
			});
		} finally {
			setIsSaving(false);
		}
	}, [editingSavedSearch, isManager, onDelete, onClose, t]);

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={onClose}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black bg-opacity-25" />
				</TransitionChild>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<DialogPanel
								className={clsx(
									'w-full max-w-md transform rounded-lg text-left align-middle shadow-xl transition-all',
									colors.classes.bgPrimary,
								)}
							>
								{/* Header */}
								<div
									className={clsx(
										'flex items-center justify-between px-6 py-4 border-b',
										colors.classes.borderDefault,
									)}
								>
									<DialogTitle
										as="h3"
										className={clsx(
											'text-lg font-medium',
											colors.classes.textPrimary,
										)}
									>
										{isEditMode ? t('Edit Search') : t('Save New Search')}
									</DialogTitle>
									<button
										type="button"
										onClick={onClose}
										className={clsx(
											'p-1 rounded hover:bg-subtle',
											colors.classes.textSecondary,
										)}
									>
										<FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
									</button>
								</div>

								{/* Body */}
								<div className="px-6 py-4 space-y-4">
									{/* Error message */}
									{errors['submit'] != null && (
										<div className="p-3 text-sm rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
											{errors['submit']}
										</div>
									)}

									{/* Name field */}
									<div>
										<label
											htmlFor="list-name"
											className={clsx(
												'block text-sm font-medium mb-1',
												colors.classes.textPrimary,
											)}
										>
											{t('Name')}
										</label>
										<input
											id="list-name"
											type="text"
											value={formData.name}
											onChange={(e) => {
												handleChange('name', e.target.value);
											}}
											placeholder={t('Enter search name')}
											className={clsx(
												'w-full px-3 py-2 text-sm border rounded-lg',
												colors.classes.bgSecondary,
												colors.classes.textPrimary,
												'focus:outline-none focus:ring-2 focus:ring-primary-500',
												'placeholder:text-faint',
												errors['name'] != null
													? 'border-danger-tint'
													: colors.classes.borderDefault,
											)}
										/>
										{errors['name'] != null && (
											<p className="mt-1 text-sm text-danger">
												{errors['name']}
											</p>
										)}
									</div>

									{/* Description field */}
									<div>
										<label
											htmlFor="list-description"
											className={clsx(
												'block text-sm font-medium mb-1',
												colors.classes.textPrimary,
											)}
										>
											{t('Description')} ({t('optional')})
										</label>
										<textarea
											id="list-description"
											value={formData.description}
											onChange={(e) => {
												handleChange('description', e.target.value);
											}}
											placeholder={t('Enter a description')}
											rows={2}
											className={clsx(
												'w-full px-3 py-2 text-sm border rounded-lg resize-none',
												colors.classes.bgSecondary,
												colors.classes.textPrimary,
												'focus:outline-none focus:ring-2 focus:ring-primary-500',
												'placeholder:text-faint',
												colors.classes.borderDefault,
											)}
										/>
									</div>

									{/* Sharing options - only for managers */}
									{isManager && (
										<>
											<div>
												<label
													className={clsx(
														'block text-sm font-medium mb-2',
														colors.classes.textPrimary,
													)}
												>
													{t('Sharing')}
												</label>
												<div className="space-y-2">
													<label className="flex items-center gap-2 cursor-pointer">
														<input
															type="radio"
															name="visibility"
															value="private"
															checked={formData.visibility === 'private'}
															onChange={() => {
																handleChange('visibility', 'private');
															}}
															className="text-primary-600 focus:ring-primary-500"
														/>
														<span
															className={clsx(
																'text-sm',
																colors.classes.textPrimary,
															)}
														>
															{t('Private (only me)')}
														</span>
													</label>

													<label className="flex items-center gap-2 cursor-pointer">
														<input
															type="radio"
															name="visibility"
															value="organization"
															checked={formData.visibility === 'organization'}
															onChange={() => {
																handleChange('visibility', 'organization');
															}}
															className="text-primary-600 focus:ring-primary-500"
														/>
														<span
															className={clsx(
																'text-sm',
																colors.classes.textPrimary,
															)}
														>
															{t('Everyone (all users)')}
														</span>
													</label>

													{availableRoles.length > 0 && (
														<label className="flex items-center gap-2 cursor-pointer">
															<input
																type="radio"
																name="visibility"
																value="role"
																checked={formData.visibility === 'role'}
																onChange={() => {
																	handleChange('visibility', 'role');
																}}
																className="text-primary-600 focus:ring-primary-500"
															/>
															<span
																className={clsx(
																	'text-sm',
																	colors.classes.textPrimary,
																)}
															>
																{t('Specific Roles')}
															</span>
														</label>
													)}
												</div>
											</div>

											{/* Role selector */}
											{formData.visibility === 'role' && (
												<div>
													<label
														className={clsx(
															'block text-sm font-medium mb-1',
															colors.classes.textPrimary,
														)}
													>
														{t('Select Roles')}
													</label>
													<RoleSelector
														availableRoles={availableRoles}
														selectedRoleIds={formData.roleIds}
														onRolesChange={(roleIds) => {
															handleChange('roleIds', roleIds);
														}}
													/>
													{errors['roleIds'] != null && (
														<p className="mt-1 text-sm text-danger">
															{errors['roleIds']}
														</p>
													)}
													{!canShareWithAny && (
														<p
															className={clsx(
																'mt-1 text-xs',
																colors.classes.textMuted,
															)}
														>
															{t(
																'You can only share with roles you are a member of',
															)}
														</p>
													)}
												</div>
											)}

											{/* Allow edit checkbox */}
											{formData.visibility !== 'private' && (
												<div>
													<label className="flex items-center gap-2 cursor-pointer">
														<input
															type="checkbox"
															checked={formData.allowEdit}
															onChange={(e) => {
																handleChange('allowEdit', e.target.checked);
															}}
															className="rounded text-primary-600 focus:ring-primary-500"
														/>
														<span
															className={clsx(
																'text-sm',
																colors.classes.textPrimary,
															)}
														>
															{t('Allow others to edit this search')}
														</span>
													</label>
													<p
														className={clsx(
															'ml-6 text-xs',
															colors.classes.textMuted,
														)}
													>
														{t(
															'If enabled, users with access can modify the search name and filters',
														)}
													</p>
												</div>
											)}
										</>
									)}

									{/* Non-manager notice */}
									{isEditMode && !isManager && (
										<p
											className={clsx(
												'text-sm italic',
												colors.classes.textMuted,
											)}
										>
											{t(
												'You can edit the name but only the search manager can change sharing settings',
											)}
										</p>
									)}
								</div>

								{/* Footer */}
								<div
									className={clsx(
										'flex items-center px-6 py-4 border-t',
										colors.classes.borderDefault,
										isEditMode && isManager ? 'justify-between' : 'justify-end',
									)}
								>
									{/* Delete button - only for managers editing */}
									{isEditMode && isManager && (
										<Button
											variant="danger"
											onClick={() => {
												setIsDeleteModalOpen(true);
											}}
											disabled={isSaving}
										>
											{t('Delete')}
										</Button>
									)}

									<div className="flex gap-3">
										<Button variant="outline" onClick={onClose}>
											{t('Cancel')}
										</Button>
										<Button
											variant="primary"
											onClick={() => {
												void handleSave();
											}}
											disabled={isSaving}
											loading={isSaving}
										>
											{isEditMode ? t('Save Changes') : t('Save Search')}
										</Button>
									</div>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>

			<DeleteConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
				}}
				onConfirm={() => {
					void handleDelete();
				}}
				isDeleting={isSaving}
				itemName={editingSavedSearch?.name}
				itemType="saved search"
			/>
		</Transition>
	);
}
